import toast from "react-hot-toast";
import { useDispatch } from "react-redux";
import { BeatLoader } from "react-spinners";
import { IoMdArrowBack } from "react-icons/io";
import { useRef, useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { sendForgotPasswordOtp, verifyForgotPasswordOtp } from "../redux/ApiSlice/ForgotPasswordSlice";
import { buildIdentifierFields } from "../utils/identifier";
// Timer duration in seconds (5 minutes)
const RESEND_DURATION = 300;

export default function EnterOTP() {
    const params = useParams();
    const { email } = params;
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [otp, setOtp] = useState(["", "", "", "", "", ""]);
    const inputRefs = useRef([]);
    const [loading, setLoading] = useState(false);
    const [timer, setTimer] = useState(RESEND_DURATION);
    const [resending, setResending] = useState(false);
    // Timer countdown effect
    useEffect(() => {
        if (timer <= 0) return;
        const timeout = setTimeout(() => {
            setTimer((prev) => prev - 1);
        }, 1000);
        return () => clearTimeout(timeout);
    }, [timer]);

    // Format timer as mm:ss
    const formatTime = (seconds) => {
        const m = Math.floor(seconds / 60).toString().padStart(2, '0');
        const s = (seconds % 60).toString().padStart(2, '0');
        return `${m}:${s}`;
    };

    // Resend OTP handler
    const handleResendOtp = async () => {

        setResending(true);
        try {
            const { email: emailField, phone: phoneField } = buildIdentifierFields(email);
            const formData = new FormData();
            formData.append("email", emailField ?? "");
            formData.append("phone", phoneField ?? "");
            const response = await dispatch(sendForgotPasswordOtp(formData));
            if (response?.payload?.message) {
                toast.success(response?.payload?.message);
            }
        } catch (error) {
            toast.error(error.message || "Failed to resend OTP");
            setResending(false);
            return error;
        }
        setTimeout(() => {
            setResending(false);
            setTimer(RESEND_DURATION);
        }, 1000);
    };

    // Handle input change
    const handleChange = (e, idx) => {
        const value = e.target.value.replace(/[^0-9]/g, "");
        if (!value) return;
        let newOtp = [...otp];
        // If user pastes all at once
        if (value.length === 6) {
            newOtp = value.split("").slice(0, 6);
            setOtp(newOtp);
            inputRefs.current[5].focus();
            return;
        }
        newOtp[idx] = value[0];
        setOtp(newOtp);
        // Move focus
        if (idx < 5 && value) {
            inputRefs.current[idx + 1].focus();
        }
    };

    // Handle backspace and arrow navigation
    const handleKeyDown = (e, idx) => {
        if (e.key === "Backspace") {
            if (otp[idx]) {
                const newOtp = [...otp];
                newOtp[idx] = "";
                setOtp(newOtp);
            } else if (idx > 0) {
                inputRefs.current[idx - 1].focus();
            }
        } else if (e.key === "ArrowLeft" && idx > 0) {
            inputRefs.current[idx - 1].focus();
        } else if (e.key === "ArrowRight" && idx < 5) {
            inputRefs.current[idx + 1].focus();
        }
    };

    // Handle paste
    const handlePaste = (e) => {
        const paste = e.clipboardData.getData("text").replace(/[^0-9]/g, "");
        if (paste.length === 6) {
            setOtp(paste.split("").slice(0, 6));
            inputRefs.current[5].focus();
            e.preventDefault();
        }

    };

    const handleVerify = async () => {
        const otpCode = otp.join("");
        if (otp.some((d) => d === "")) {
            toast.error("Please enter all 6 digits");
            return;
        }
        const data = {
            ...buildIdentifierFields(email),
            otp: otpCode
        };
        setLoading(true);
        try {
            const response = await dispatch(verifyForgotPasswordOtp(data));
            if (response?.payload?.otp && Array.isArray(response.payload.otp)) {
                toast.error(response.payload.otp[0]);
                setLoading(false);
            } else if (response.type === "verifyForgotPasswordOtp/fulfilled") {
                toast.success("OTP verified successfully!");
                setLoading(false);
                setTimeout(() => {
                    navigate(`/changepassword/${otpCode}/${email}`);
                }, 1000);
            }
            else {
                toast.error("OTP verification failed. Please try again.");
                setLoading(false);
            }
        }
        catch (error) {
            setLoading(false);
            console.error("Error verifying OTP:", error);
            toast.error("Something went wrong. Please try again.");
        }
    };

    return (
        <div className="h-full w-full bg-white dark:bg-[#1E1D21]">
            <div className="sticky top-0">
                <div className="relative flex items-center justify-center h-12 bg-[#BDE6FF] rounded-b-xl px-2 z-10">
                    <IoMdArrowBack
                        onClick={() => navigate("/login")}
                        className="absolute left-2 top-1/2 transition-transform -translate-y-1/2 text-[24px] text-black cursor-pointer"
                    />
                    <p className="text-center font-bold text-[16px] text-black flex-grow">Forgot password</p>
                </div>
            </div>

            <div className="w-full h-full p-4">
                <p className="text-[18px] font-semibold text-black dark:text-white">
                    Reset your password
                </p>
                <p className="text-[11px] mt-1 font-semibold text-gray-500 dark:text-gray-400">
                    We sent a 6-digit code to {email}
                </p>
                <form className="w-full mt-5 flex gap-3 items-center justify-between" autoComplete="off">
                    {otp.map((digit, idx) => (
                        <input
                            key={idx}
                            type="text"
                            maxLength={1}
                            value={digit}
                            inputMode="numeric"
                            onPaste={handlePaste}
                            onChange={e => handleChange(e, idx)}
                            onKeyDown={e => handleKeyDown(e, idx)}
                            ref={el => inputRefs.current[idx] = el}
                            className="h-10 w-10 text-center text-xl font-bold border border-gray-400 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-[#1E1D21] text-black dark:text-white"
                        />
                    ))}
                </form>

                <p className="text-[11px] mt-5 text-center font-semibold text-gray-500 dark:text-gray-400">
                    Code expires in: <span className="text-[#0A96C1]">{formatTime(timer)}</span>
                </p>

                <button
                    disabled={resending || timer > 0}
                    onClick={handleResendOtp}
                    className="text-[11px] mt-5 font-semibold text-center w-full flex justify-center items-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {resending ? (
                        "Resending..."
                    ) : (
                        <div className="flex items-center">
                            <p className="text-gray-800 dark:text-gray-200">Didn't receive a code?</p>
                            <p className={`ml-1 underline ${timer > 0 ? "text-gray-400" : "text-[#0A96C1] dark:text-[#0da7d6] cursor-pointer"}`}>Resend</p>
                        </div>
                    )}
                </button>

                <button
                    type="button"
                    onClick={() => handleVerify()}
                    className="h-12 text-[16px] font-semibold px-3 bg-[#BDE6FF] text-black rounded-xl w-full my-6 disabled:bg-gray-300 disabled:cursor-not-allowed disabled:opacity-50"
                >
                    {loading ? <BeatLoader
                        size={10}
                        color={"#fff"}
                        loading={loading}
                    /> : "Confirm"
                    }
                </button>
            </div>
        </div>
    );
}